import Big from 'big.js';
import { action, computed, makeObservable, observable, reaction } from 'mobx';
import { YesNoState } from '@alfa-retail-react/components/yes-no-question';

import { OptionShape } from 'app/types/reference';
import { Employment } from 'app/model/Employment';
import { AddressFormStore } from 'app/logic/address/AddressFormStore';
import { Reference } from 'app/model/Reference';
import { CardOrder } from 'app/model/CardOrder';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';
import { ValidationRules } from 'app/form/ValidationRules';

/**
 * Хранилище данных формы ввода сведений о работе.
 */
export class EmploymentFormStore {
    readonly OTHER_ACTIVITY_FIELD_CODE = '15';

    @observable
    activityKindCode: string;

    @observable
    unp: string;

    @observable
    incomeAmount?: Big;

    @observable
    activityFieldCode: string;

    experience: string;

    @observable
    soleOwner: YesNoState;

    @observable
    sameAddress: YesNoState;

    deliveryAddressFormStore: AddressFormStore;

    unpMemo = {
        sole: '',
        selfEmployed: '',
    };

    unpMemoAccessor: 'sole' | 'selfEmployed';

    constructor(
        private readonly cardOrder: CardOrder,
        private readonly _employmentReference: Array<Reference>,
        private readonly _industriesReference: Array<Reference>,
        private readonly _experienceReference: Array<Reference>,
        private readonly _streetTypesReference: Array<Reference>,
        private readonly _country: Reference,
        private readonly _localityTypesStore: LocalityTypeStore,
    ) {
        makeObservable(this);
        reaction(
            () => this.isAttorney,
            () => this.setDefaultAttorneyActivityField(),
        );

        this.activityKindCode =
            cardOrder.client.employment?.activityKindCode ===
            Employment.ActivityCode.default
                ? null
                : cardOrder.client.employment?.activityKindCode;

        this.unp = cardOrder.client.employment?.unp;
        this.incomeAmount =
            cardOrder.client.employment?.incomeAmount ?? new Big(0);
        this.activityFieldCode = cardOrder.client.employment?.activityFieldCode;
        this.experience = cardOrder.client.employment?.experience;

        this.setInitialFilledData(this.unp);

        const isSoleActivity =
            !!cardOrder.client.employment &&
            !!cardOrder.client.employment.activityKindCode &&
            cardOrder.client.employment.activityKindCode !==
                Employment.ActivityCode.default;

        this.soleOwner = isSoleActivity ? YesNoState.YES : YesNoState.NO;

        if (!cardOrder.deliveryAddress) {
            this.sameAddress = YesNoState.YES;
        } else {
            this.sameAddress = cardOrder.deliveryAddress.equals(
                cardOrder.client.residentialAddress,
            )
                ? YesNoState.YES
                : YesNoState.NO;
        }

        this.deliveryAddressFormStore = new AddressFormStore(
            cardOrder.deliveryAddress,
            _streetTypesReference,
            _country,
            this._localityTypesStore,
        );
    }

    initialize() {
        return this.deliveryAddressFormStore.initialize();
    }

    @computed
    get isUnemployedPensioner() {
        return (
            this.soleOwner === YesNoState.YES &&
            this.activityKindCode ===
                Employment.ActivityCode.unemployed_pensioner
        );
    }

    @computed
    get isAttorney() {
        return (
            this.soleOwner === YesNoState.YES &&
            this.activityKindCode === Employment.ActivityCode.attorney
        );
    }

    get isSelfEmployed() {
        return this.activityKindCode === Employment.ActivityCode.selfEmployed;
    }

    @computed
    get availableUNP() {
        const isUnpOwner = Employment.isActivityHasUNP(this.activityKindCode);

        return this.soleOwner === YesNoState.YES && isUnpOwner;
    }

    get employmentReference(): Array<OptionShape> {
        return this._employmentReference
            .filter(
                (reference) => reference.id !== Employment.ActivityCode.default,
            )
            .map((reference) => {
                return { key: reference.id, content: reference.name };
            });
    }

    get industriesReference(): Array<OptionShape> {
        return this._industriesReference.map((reference) => {
            return { key: reference.id, content: reference.name };
        });
    }

    get experienceReference(): Array<OptionShape> {
        return this._experienceReference.map((reference) => {
            return { key: reference.id, content: reference.name };
        });
    }

    /**
     * Устанавливает тип деятельности и подставляет соответствующий УНП при наличии его в памяти
     * @param activityKindCode
     */
    @action
    setActivityKindCode(activityKindCode: string) {
        this.activityKindCode = activityKindCode;

        if (Employment.isActivityHasUNP(activityKindCode)) {
            const isSelfEmployed =
                activityKindCode === Employment.ActivityCode.selfEmployed;

            this.updateUnpMemoAccessor(isSelfEmployed);
            this.setUnp(this.unpMemo[this.unpMemoAccessor]);
        } else {
            this.setUnp('');
        }
    }

    @action
    setSoleOwner(soleOwner: YesNoState) {
        this.soleOwner = soleOwner;
    }

    @action
    setSameAddress(sameAddress: YesNoState) {
        this.sameAddress = sameAddress;
    }

    @action
    setActivityFieldCode(activityFieldCode: string) {
        this.activityFieldCode = activityFieldCode;
    }

    setDefaultAttorneyActivityField() {
        this.setActivityFieldCode(this.OTHER_ACTIVITY_FIELD_CODE);
    }

    /**
     * Устанавливает текущий УНП и вызывает сохранение его в памяти
     * @param unp
     */
    @action
    setUnp = (unp: string) => {
        this.unp = unp;

        if (Employment.isActivityHasUNP(this.activityKindCode)) {
            this.setUnpMemo(unp);
        }
    };

    /**
     * Устанавливает селектор УНП в зависимости от типа деятельности (ИП, Работник ИП / Самозанятый)
     * @param isSelfEmployed
     */
    updateUnpMemoAccessor(isSelfEmployed: boolean) {
        this.unpMemoAccessor = isSelfEmployed ? 'selfEmployed' : 'sole';
    }

    /**
     * Сохраняет в памяти УНП для текущего типа деятельности
     * @param unp
     */
    setUnpMemo(unp: string) {
        this.unpMemo[this.unpMemoAccessor] = unp;
    }

    /**
     * Сохраняет в памяти УНП для вида деятельности, к которому он подходит по характерным признакам
     * @param unp
     */
    @action
    setInitialFilledData(unp?: string): void {
        if (!unp) {
            return;
        }

        const isSelfEmployed =
            ValidationRules.unpSelfEmployedWithCharsRegex.test(unp);

        /**
         * XSEC2C-3098 У самозанятых УНП может так же быть из 9 цифр
         * Но по-умолчанию считаем такой УНП за ИП
         */
        if (!isSelfEmployed) {
            this.updateUnpMemoAccessor(true);
            this.setUnpMemo(unp);
        }

        this.updateUnpMemoAccessor(isSelfEmployed);
        this.setUnpMemo(unp);
    }
}
